import { useState, useEffect } from "react";
import { api } from "@/App";
import { Plus, Trash2, Save, FileText, Workflow, GripVertical, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const COLORS = [
  { value: "slate", label: "Gris", class: "bg-slate-100 text-slate-700" },
  { value: "amber", label: "Jaune", class: "bg-amber-100 text-amber-700" },
  { value: "orange", label: "Orange", class: "bg-orange-100 text-orange-700" },
  { value: "blue", label: "Bleu", class: "bg-blue-100 text-blue-700" },
  { value: "purple", label: "Violet", class: "bg-purple-100 text-purple-700" },
  { value: "emerald", label: "Vert", class: "bg-emerald-100 text-emerald-700" },
  { value: "red", label: "Rouge", class: "bg-red-100 text-red-700" },
  { value: "pink", label: "Rose", class: "bg-pink-100 text-pink-700" },
  { value: "cyan", label: "Cyan", class: "bg-cyan-100 text-cyan-700" },
];

export default function PrestationsConfig() {
  const [prestations, setPrestations] = useState([]);
  const [allWorkflowSteps, setAllWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [workflowDialog, setWorkflowDialog] = useState(false);
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const [newPrestation, setNewPrestation] = useState({
    label: "",
    has_workflow: false,
    color: "slate"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prestRes, stepsRes] = await Promise.all([
        api.get("/prestations"),
        api.get("/workflow-steps")
      ]);
      setPrestations(prestRes.data);
      setAllWorkflowSteps(stepsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const fetchPrestations = async () => {
    try {
      const response = await api.get("/prestations");
      setPrestations(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const handleAddPrestation = async () => {
    if (!newPrestation.label) {
      toast.error("Entrez un nom pour la prestation");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/prestations", {
        name: newPrestation.label.toLowerCase().replace(/\s+/g, "_"),
        label: newPrestation.label,
        has_workflow: newPrestation.has_workflow,
        color: newPrestation.color
      });
      toast.success("Prestation ajoutée");
      setDialogOpen(false);
      setNewPrestation({ label: "", has_workflow: false, color: "slate" });
      fetchPrestations();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleDeletePrestation = async (prestId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette prestation ?")) return;
    
    try {
      await api.delete(`/prestations/${prestId}`);
      toast.success("Prestation supprimée");
      fetchPrestations();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleUpdatePrestation = async (prestId, field, value) => {
    try {
      await api.put(`/prestations/${prestId}`, { [field]: value });
      fetchPrestations();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const openWorkflowConfig = (prestation) => {
    setSelectedPrestation(prestation);
    setSelectedSteps(prestation.workflow_steps || []);
    setWorkflowDialog(true);
  };

  const toggleStepSelection = (stepId) => {
    setSelectedSteps(prev => {
      if (prev.includes(stepId)) {
        return prev.filter(id => id !== stepId);
      }
      return [...prev, stepId];
    });
  };

  const handleSaveWorkflowSteps = async () => {
    if (!selectedPrestation) return;
    setSaving(true);
    try {
      await api.put(`/prestations/${selectedPrestation.id}`, {
        workflow_steps: selectedSteps
      });
      toast.success("Workflow personnalisé enregistré");
      setWorkflowDialog(false);
      fetchPrestations();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const getColorClass = (color) => {
    const c = COLORS.find(col => col.value === color);
    return c?.class || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="prestations-config">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Types de Prestations</h1>
        <p className="text-slate-600 mt-1">Créez et gérez vos différents types d'interventions</p>
      </div>

      {/* Prestations List */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Prestations disponibles
            </CardTitle>
            <CardDescription>Définissez les types d'intervention que vous proposez</CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle prestation
          </Button>
        </CardHeader>
        <CardContent>
          {prestations.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucune prestation configurée</p>
          ) : (
            <div className="space-y-3">
              {prestations.map((prestation) => (
                <div
                  key={prestation.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 bg-white"
                  data-testid={`prestation-${prestation.id}`}
                >
                  <Badge className={`${getColorClass(prestation.color)} min-w-[120px] justify-center`}>
                    {prestation.label}
                  </Badge>
                  
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <Input
                      value={prestation.label}
                      onChange={(e) => handleUpdatePrestation(prestation.id, "label", e.target.value)}
                      placeholder="Nom de la prestation"
                    />
                    
                    <Select
                      value={prestation.color}
                      onValueChange={(v) => handleUpdatePrestation(prestation.id, "color", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLORS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded ${color.class}`} />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={prestation.has_workflow}
                        onCheckedChange={(v) => handleUpdatePrestation(prestation.id, "has_workflow", v)}
                      />
                      <Label className="text-sm flex items-center gap-1">
                        <Workflow className="h-4 w-4" />
                        Workflow
                      </Label>
                    </div>
                    
                    {prestation.has_workflow && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openWorkflowConfig(prestation)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        <Settings2 className="h-4 w-4 mr-1" />
                        Étapes ({prestation.workflow_steps?.length || 0})
                      </Button>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeletePrestation(prestation.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Workflow className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">À propos du Workflow</p>
              <p className="text-sm text-blue-700 mt-1">
                Activez le workflow pour les prestations qui nécessitent plusieurs étapes (comme la carrosserie). 
                Les prestations sans workflow seront traitées comme des RDV simples (comme la mécanique).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Prestation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle prestation</DialogTitle>
            <DialogDescription>Créez un nouveau type d'intervention</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de la prestation *</Label>
              <Input
                placeholder="Ex: Vitrage, Climatisation, Électricité..."
                value={newPrestation.label}
                onChange={(e) => setNewPrestation({...newPrestation, label: e.target.value})}
                data-testid="input-prestation-label"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Couleur</Label>
              <Select
                value={newPrestation.color}
                onValueChange={(v) => setNewPrestation({...newPrestation, color: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${color.class}`} />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <Switch
                checked={newPrestation.has_workflow}
                onCheckedChange={(v) => setNewPrestation({...newPrestation, has_workflow: v})}
              />
              <div>
                <Label className="font-medium">Utilise le workflow</Label>
                <p className="text-xs text-slate-500">
                  Activez si cette prestation passe par plusieurs étapes
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAddPrestation}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-prestation-btn"
            >
              {saving ? <div className="spinner" /> : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Configuration Dialog */}
      <Dialog open={workflowDialog} onOpenChange={setWorkflowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Workflow personnalisé
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les étapes pour "{selectedPrestation?.label}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Cochez les étapes et ordonnez-les selon le workflow souhaité. Si aucune étape n'est sélectionnée, le workflow par défaut sera utilisé.
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {allWorkflowSteps.map((step) => {
                const isSelected = selectedSteps.includes(step.id);
                return (
                  <div
                    key={step.id}
                    onClick={() => toggleStepSelection(step.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected 
                        ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox checked={isSelected} />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{step.label}</p>
                      <p className="text-xs text-slate-500">Spécialité: {step.specialite}</p>
                    </div>
                    {isSelected && (
                      <Badge variant="outline" className="text-blue-600">
                        #{selectedSteps.indexOf(step.id) + 1}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
            
            {selectedSteps.length > 0 && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-700 mb-2">Ordre des étapes:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedSteps.map((stepId, idx) => {
                    const step = allWorkflowSteps.find(s => s.id === stepId);
                    return (
                      <Badge key={stepId} variant="outline" className="text-xs">
                        {idx + 1}. {step?.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkflowDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveWorkflowSteps}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <div className="spinner" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
